import { LanguageCode } from "../customers/customerlanguages";
import { NoTranslateCell } from "../file-viewer/excel/types";
import { TranslationInfo } from "../machine-translation/previously-machine-translated-resources/PreviouslyMachineTranslatedRailList";
import api from "./api";
import { TaskQueue } from "./taskQueueApi";
import { TaskId } from "./types";
import { SSEClient } from "./SSEApi";
import { Job } from "../customers/jobs/types";

export const BASE_URL = "/api/_internal/mt/";

export type Url = string;

export type DoneTranslateFileResponse = {
  url: Url;
  target_file_path: string;
  original_target_file_name: string;
};

export type DoneTranslateTextResponse = {
  translated_text: string;
  translation_event_id: number;
  text_block_id?: string;
};

export type TranslationAutoSaveContext = {
  product_id: string;
  source_text_block_id: string;
  document_section_id: number;
};

export type TranslateFileResponse = {
  task_ids: { [language: string]: TaskId };
  path_to_source_file: string;
  job_ids?: { [language: string]: number };
};

export type DownloadFilesResponse = {
  url: Url;
};

export type TranslateTextResponse = {
  source_language: string; // useful when source was auto-detected
  task_ids: { [language: string]: TaskId };
};

export type DetectLanguageResponse = {
  detected_language: LanguageCode;
};

export type JobStreamClosersRef = { current: (() => void)[] };

export class MachineTranslateTaskQueue<
  T = DoneTranslateFileResponse | DoneTranslateTextResponse
> extends TaskQueue<T> {
  private jobStreamClosersRef: JobStreamClosersRef | null = null;

  private jobStreamClosersInternal: (() => void)[] = [];

  constructor(
    token: string,
    pollInterval?: number,
    jobStreamClosersRef?: JobStreamClosersRef
  ) {
    super(token, pollInterval, "Translation Cancelled");
    this.jobStreamClosersRef = jobStreamClosersRef ?? null;
  }

  // This enables us to wait for a specific language to be translated
  private requests: {
    [language: string]: Promise<T>;
  } = {};

  private getJobStreamClosers(): (() => void)[] {
    if (this.jobStreamClosersRef) return this.jobStreamClosersRef.current;
    return this.jobStreamClosersInternal;
  }

  private setJobStreamClosers(closers: (() => void)[]): void {
    if (this.jobStreamClosersRef) {
      this.jobStreamClosersRef.current = closers;
    } else {
      this.jobStreamClosersInternal = closers;
    }
  }

  async waitUntilTranslateLanguageIsDone(language: string): Promise<T> {
    return this.requests[language].finally(() => {
      delete this.requests[language];
    });
  }

  cancelAll(): void {
    this.getTaskIds().map((taskId) => {
      this.cancelTask(taskId);
    });
    const closers = this.getJobStreamClosers();
    closers.forEach((close) => close());
    this.setJobStreamClosers([]);
  }

  private extractJobResult(job: Job): Promise<DoneTranslateFileResponse> {
    if (
      job.status_from_tasks_result === "FAILURE" ||
      job.last_run_task?.status === "FAILURE"
    ) {
      const errorMsg =
        job.last_run_task?.result?.error || "Job failed";
      return Promise.reject(new Error(errorMsg));
    }
    const lastRunTask = job.last_run_task;
    if (!lastRunTask || !lastRunTask.result) {
      return Promise.reject(new Error("Job completed without result"));
    }
    const result = lastRunTask.result as {
      url?: string;
      target_file_path?: string;
      original_target_file_name?: string;
      translation_task_id?: string;
    };
    if (
      result.url &&
      result.target_file_path &&
      result.original_target_file_name
    ) {
      return Promise.resolve({
        url: result.url,
        target_file_path: result.target_file_path,
        original_target_file_name: result.original_target_file_name,
      });
    }
    if (result.translation_task_id) {
      this.addTaskId(
        result.translation_task_id,
        "Machine Translate File (from job)"
      );
      return this.waitUntilTaskIsDone(result.translation_task_id).then(
        (taskResult) => {
          const fileResult = taskResult as {
            url?: string;
            target_file_path?: string;
            original_target_file_name?: string;
          };
          if (
            fileResult.url &&
            fileResult.target_file_path &&
            fileResult.original_target_file_name
          ) {
            return {
              url: fileResult.url,
              target_file_path: fileResult.target_file_path,
              original_target_file_name: fileResult.original_target_file_name,
            };
          }
          throw new Error("Task result missing required file fields");
        }
      );
    }
    return Promise.reject(new Error("Job result missing required fields"));
  }

  private waitUntilJobsBatchComplete(
    jobIdsByLanguage: { [language: string]: number },
    onJobUpdate?: (language: string, job: Job) => void
  ): { [language: string]: Promise<DoneTranslateFileResponse> } {
    const jobIds = Object.values(jobIdsByLanguage);
    const jobIdToLanguage: { [jobId: number]: string } = {};
    Object.entries(jobIdsByLanguage).forEach(([lang, id]) => {
      jobIdToLanguage[id] = lang;
    });

    const resolvers: {
      [language: string]: {
        resolve: (r: DoneTranslateFileResponse) => void;
        reject: (e: Error) => void;
      };
    } = {};
    const promises: { [language: string]: Promise<DoneTranslateFileResponse> } =
      {};
    Object.keys(jobIdsByLanguage).forEach((language) => {
      promises[language] = new Promise((resolve, reject) => {
        resolvers[language] = { resolve, reject };
      });
    });

    const sseClient = new SSEClient<{ jobs: { [jobId: string]: Job } }>(
      this.token
    );
    const closer = (): void => sseClient.close();
    this.getJobStreamClosers().push(closer);

    let finished = false;
    const finish = (payload: { jobs: { [jobId: string]: Job } }): void => {
      if (finished) return;
      finished = true;
      sseClient.close();
      const jobs = payload.jobs || {};
      const noData = Object.keys(jobs).length === 0;
      Object.keys(resolvers).forEach((language) => {
        const jobId = jobIdsByLanguage[language];
        const job = jobId ? jobs[String(jobId)] : undefined;
        if (job) {
          this.extractJobResult(job)
            .then((r) => resolvers[language].resolve(r))
            .catch((e) => resolvers[language].reject(e));
        } else {
          resolvers[language].reject(
            new Error(
              noData
                ? "Job stream closed without data"
                : "Job data missing for language"
            )
          );
        }
      });
    };

    let lastPayload: { jobs: { [jobId: string]: Job } } = { jobs: {} };
    try {
      const url = `/api/_internal/customer/jobs/batch/stream/?job_ids=${jobIds.join(",")}`;
      sseClient.connect(
        url,
        (data) => {
          const payload = data as {
            jobs?: { [jobId: string]: Job };
            close?: boolean;
          };
          if (payload.jobs) {
            lastPayload = { jobs: payload.jobs };
            if (onJobUpdate) {
              Object.entries(payload.jobs).forEach(([jobIdStr, job]) => {
                const language = jobIdToLanguage[Number(jobIdStr)];
                if (language) onJobUpdate(language, job);
              });
            }
            if (payload.close) {
              finish(lastPayload);
            }
          }
        },
        () => {
          finish(lastPayload);
        },
        (error, msg) => {
          console.error("Batch job stream error", error, msg);
          sseClient.close();
          Object.values(resolvers).forEach((r) =>
            r.reject(new Error(msg || "Job stream error"))
          );
        }
      );
    } catch (error) {
      sseClient.close();
      Object.values(resolvers).forEach((r) =>
        r.reject(
          error instanceof Error ? error : new Error(String(error))
        )
      );
    }

    return promises;
  }

  private async waitUntilJobIsDone(
    jobId: number,
    onUpdate?: (job: Job) => void
  ): Promise<DoneTranslateFileResponse> {
    return new Promise((resolve, reject) => {
      const sseClient = new SSEClient<Job & { close?: boolean }>(this.token);
      let finished = false;
      let lastJobData: Job | null = null;

      const closer = (): void => {
        sseClient.close();
      };
      const closers = this.getJobStreamClosers();
      closers.push(closer);

      const finish = (jobData?: Job & { close?: boolean }): void => {
        if (finished) return;
        finished = true;

        const dataToUse = jobData || lastJobData;

        try {
          if (!dataToUse) {
            reject(new Error("Job stream closed without data"));
            return;
          }

          // Check if job or task failed
          if (
            dataToUse.status_from_tasks_result === "FAILURE" ||
            dataToUse.last_run_task?.status === "FAILURE"
          ) {
            const errorMsg =
              dataToUse.last_run_task?.result?.error || "Job failed";
            reject(new Error(errorMsg));
            return;
          }

          const lastRunTask = dataToUse.last_run_task;
          if (!lastRunTask || !lastRunTask.result) {
            reject(new Error("Job completed without result"));
            return;
          }

          // Extract result from assemble_file_task
          const result = lastRunTask.result as {
            url?: string;
            target_file_path?: string;
            original_target_file_name?: string;
            customer_file_id?: number;
            errors?: unknown[];
            reports?: unknown;
            translation_task_id?: string;
          };

          if (
            result.url &&
            result.target_file_path &&
            result.original_target_file_name
          ) {
            const doneResponse: DoneTranslateFileResponse = {
              url: result.url,
              target_file_path: result.target_file_path,
              original_target_file_name: result.original_target_file_name,
            };
            resolve(doneResponse);
            return;
          }

          if (result.translation_task_id) {
            this.addTaskId(
              result.translation_task_id,
              "Machine Translate File (from job)"
            );
            this.waitUntilTaskIsDone(result.translation_task_id)
              .then((taskResult) => {
                const fileResult = taskResult as {
                  url?: string;
                  target_file_path?: string;
                  original_target_file_name?: string;
                };
                if (
                  fileResult.url &&
                  fileResult.target_file_path &&
                  fileResult.original_target_file_name
                ) {
                  resolve({
                    url: fileResult.url,
                    target_file_path: fileResult.target_file_path,
                    original_target_file_name:
                      fileResult.original_target_file_name,
                  });
                } else {
                  reject(new Error("Task result missing required file fields"));
                }
              })
              .catch((error) => {
                reject(
                  error instanceof Error
                    ? error
                    : new Error("Failed to get task result: " + String(error))
                );
              });
            return;
          }

          reject(new Error("Job result missing required fields"));
        } catch (error) {
          reject(error instanceof Error ? error : new Error(String(error)));
        }
      };

      try {
        sseClient.connect(
          `/api/_internal/customer/jobs/${jobId}/stream/`,
          (data) => {
            const jobData = data as Job & { close?: boolean };

            lastJobData = jobData;

            if (onUpdate) {
              onUpdate(jobData);
            }

            if (jobData.close) {
              finish(jobData);
            }
          },
          () => {
            // Stream ended by server; use last known job data if available
            finish();
          },
          (error, msg) => {
            console.error("Job stream error", error, msg);
            sseClient.close();
            reject(new Error(msg || "Job stream error"));
          }
        );
      } catch (error) {
        console.error("Unable to open SSE stream", error);
        sseClient.close();
        reject(error instanceof Error ? error : new Error(String(error)));
      }
    });
  }

  async translateFile(
    sourceFile: File,
    sourceLanguage: string | null,
    targetLanguages: string[],
    sourceContext: string,
    configGroupId?: number,
    translateFileInfo?: {
      noTranslateCells: Omit<NoTranslateCell, "locked">[];
      languageCells: { [language: string]: number };
    },
    onJobUpdate?: (language: string, job: Job) => void
  ): Promise<{
    path_to_source_file: string;
    job_ids?: { [language: string]: number };
  }> {
    const formData = new FormData();
    formData.append("source_file", sourceFile, sourceFile.name);
    formData.append("source_language", sourceLanguage || "");
    for (const targetLanguage of targetLanguages) {
      formData.append("target_languages", targetLanguage);
    }
    if (configGroupId) {
      formData.append("config_group_id", configGroupId.toString());
    }
    if (sourceContext) {
      formData.append("source_context", sourceContext);
    }
    if (translateFileInfo) {
      formData.append("translate_file_info", JSON.stringify(translateFileInfo));
    }
    const response = await api.post<TranslateFileResponse>(
      `${BASE_URL}translate/file`,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
          token: this.token,
        },
      }
    );

    if (response.data.job_ids) {
      this.setJobStreamClosers([]);
      const batchPromises = this.waitUntilJobsBatchComplete(
        response.data.job_ids,
        onJobUpdate
      );
      Object.entries(batchPromises).forEach(([language, promise]) => {
        this.requests[language] = promise as unknown as Promise<T>;
      });
    } else {
      Object.entries(response.data.task_ids).forEach(([language, taskId]) => {
        this.addTaskId(taskId, "Machine Translate File");
        this.requests[language] = this.waitUntilTaskIsDone(taskId);
      });
    }

    return {
      path_to_source_file: response.data.path_to_source_file,
      job_ids: response.data.job_ids,
    };
  }

  async translateFileAndSendToProofreading(
    sourceFile: File,
    sourceLanguage: string | null,
    targetLanguages: string[],
    sourceContext: string,
    emailDelivery: string,
    isUrgent: boolean,
    deliverInOneFile: boolean,
    additionalInstructions: string,
    configGroupId?: number,
    translateFileInfo?: {
      noTranslateCells: Omit<NoTranslateCell, "locked">[];
      languageCells: { [language: string]: number };
    }
  ): Promise<void> {
    const formData = new FormData();
    formData.append("source_file", sourceFile, sourceFile.name);
    formData.append("source_language", sourceLanguage || "");
    for (const targetLanguage of targetLanguages) {
      formData.append("target_languages", targetLanguage);
    }
    if (configGroupId) {
      formData.append("config_group_id", configGroupId.toString());
    }
    if (sourceContext) {
      formData.append("source_context", sourceContext);
    }
    formData.append("email_delivery", emailDelivery);
    formData.append("is_urgent", isUrgent.toString());
    formData.append("deliver_in_one_file", deliverInOneFile.toString());
    formData.append("additional_instructions", additionalInstructions);
    if (translateFileInfo) {
      formData.append("translate_file_info", JSON.stringify(translateFileInfo));
    }
    await api.post<unknown>(
      `${BASE_URL}translate/file-and-send-to-proofreading`,
      formData,
      {
        headers: {
          token: this.token,
        },
      }
    );
  }

  async downloadFiles(
    sourceFilePath: string,
    targetFilePaths: string[]
  ): Promise<string> {
    const data = {
      source_file_path: sourceFilePath,
      target_file_paths: targetFilePaths,
    };
    const response = await api.post<DownloadFilesResponse>(
      `${BASE_URL}translate/download-files`,
      data,
      {
        headers: {
          token: this.token,
        },
      }
    );
    return response.data.url;
  }

  async translateText(
    sourceText: string,
    sourceContext: string,
    sourceLanguage: string | null,
    targetLanguages: string[],
    configGroupId?: number,
    autoSaveContext?: TranslationAutoSaveContext
  ): Promise<string> {
    const data = {
      source_text: sourceText,
      source_context: sourceContext,
      source_language: sourceLanguage || "",
      target_languages: targetLanguages,
      config_group_id: configGroupId,
      ...(autoSaveContext && {
        product_id: autoSaveContext.product_id,
        source_text_block_id: autoSaveContext.source_text_block_id,
        document_section_id: autoSaveContext.document_section_id,
      }),
    };
    const response = await api.post<TranslateTextResponse>(
      `${BASE_URL}translate/text`,
      data,
      {
        headers: {
          token: this.token,
        },
      }
    );
    Object.entries(response.data.task_ids).forEach(([language, taskId]) => {
      this.addTaskId(taskId, "Machine Translate Text");
      this.requests[language] = this.waitUntilTaskIsDone(taskId);
    });

    return response.data.source_language;
  }
}

export async function detectLanguage(
  token: string,
  text: string
): Promise<LanguageCode> {
  const response = await api.post<DetectLanguageResponse>(
    `${BASE_URL}detect-language`,
    { text },
    {
      headers: { token },
    }
  );
  return response.data.detected_language;
}

export async function getPreviousMachineTranslations(
  token: string,
  {
    start,
    end,
    query_string,
    language_code,
    type = "text",
  }: {
    start: string;
    end: string;
    query_string?: string;
    language_code?: LanguageCode;
    type?: "text" | "file";
  }
): Promise<TranslationInfo[]> {
  const response = await api.get<TranslationInfo[]>(
    BASE_URL + "previous-translations",
    {
      headers: { token },
      params: { start, end, query_string, language_code, type },
    }
  );

  return response.data;
}

export async function modifyTranslatedText(
  token: string,
  translationEventId: number,
  modifiedText: string
): Promise<unknown> {
  const response = await api.put(
    BASE_URL + "modify-translated-text",
    {
      translation_event_id: translationEventId,
      modified_text: modifiedText,
    },
    {
      headers: { token },
    }
  );

  return response.data;
}
